import React, { createContext, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

export const AppContext = createContext();

const AppProvider = ({ children }) => {
    const [state, setState] = useState({
        cuit: null,
        id: null,
        emails: [],
        data: null,
        token: {},
        arrepentimientoCUIT: null,
        plan: null,
        easyTrackingEnabled: false,
        haberNetoEnabled: false,
        razonSocialStellantisEnabled: false,
        cargaOferta: null,
        planes: [],
        planesHistoricos: null,
        planDetails: null,
        selectedPlan: null,
        reintegros: { MuestraBoton: false, URL: "" }
    });
    const [loadingPlanDetails, setLoadingPlanDetails] = useState(false);

    const history = useHistory();
    const documento = sessionStorage.getItem("dtdc");
    const notificacionId = sessionStorage.getItem("ntid");
    const tokenLogin = atob(sessionStorage.getItem('dttk'));

    const setSelectedPlan = async (plan) => {
        if (!plan) return;

        // Update selectedPlan in state and reset planDetails
        setState(prevState => ({
            ...prevState,
            selectedPlan: { ...plan },
            planDetails: null, // Reset planDetails while fetching
            cargaOferta: null, // Clear cargaOferta when selecting a new plan
            reintegros: { MuestraBoton: false, URL: "" },
        }));

        sessionStorage.setItem("dtpln", btoa(plan.Id)); // Save selected plan in sessionStorage

        try {
            // Fetch and update plan details
            const response = await fetch(`/planes_historicos/${plan.CodigoPlan}`);
            const result = await response.json();

            setState(prevState => ({
                ...prevState,
                planDetails: {
                    ...plan,
                    TipoPlan: result.descripcion,
                    ModeloAhorro: result.modeloAhorro,
                    EstadoPlan: result.estadoPlan,
                    ConcesionarioActual: result.concesionarioActual,
                },
            }));

            // Fetch cargaOferta if EstadoPlan is "Ahorrista"
            if (plan.EstadoPlan === "Ahorrista") {
                const cargaOfertaResponse = await fetch('/api/show_endpoints/cargaoferta');
                const cargaOfertaEndpoint = await cargaOfertaResponse.json();
                setState(prevState => ({
                    ...prevState,
                    cargaOferta: `${cargaOfertaEndpoint.url_base}${cargaOfertaEndpoint.url_variable}`,
                }));
            }

            // Redirect to /inicio after updating plan details
            history.push("/inicio");
        } catch (error) {
            console.error("Error fetching plan details or cargaOferta:", error);


            setState(prevState => ({
                ...prevState,
                reintegros: { MuestraBoton: false, URL: "" },
            }));
        }
    };

    const fetchStatusFlags = async () => {
        try {
            const response = await fetch('/get_status_flags');
            const data = await response.json();

            setState(prevState => ({
                ...prevState,
                easyTrackingEnabled: data.easyTracking === "1",
                haberNetoEnabled: data.haberNetoEnabled === "1",
                reintegroEnabled: data.botonReintegroEnabled === "1",
                totalValorNetoEnabled: data.totalValorNetoEnabled === "1",
                widgetWhatsappEnabled: data.widgetWhatsappEnabled === "1",
                urlWidgetWhatsapp: data.urlWidgetWhatsapp || '',
                stepsAdministrativosEnabled: data.stepsAdministrativos === "1",
                stepsLogisticosEnabled: data.stepsLogisticos === "1",
                razonSocialStellantisEnabled: data.razonSocialStellantisEnabled === "1",
            }));
        } catch (error) {
            console.error('Error fetching status flags:', error);
        }
    };

    const fetchPlanDetails = async (plan) => {
        if (!plan) return;

        setLoadingPlanDetails(true);
        try {
            const response = await fetch(`/planes_historicos/${plan.CodigoPlan}`);
            const result = await response.json();

            setState(prevState => ({
                ...prevState,
                planDetails: {
                    ...plan,
                    TipoPlan: result.descripcion,
                },
            }));
        } catch (error) {
            console.error("Error fetching plan details:", error);
        } finally {
            setLoadingPlanDetails(false);
        }
    };

    useEffect(() => {
        if (documento) {
            const fetchUserData = async () => {
                try {
                    const endpoint = await fetch('/api/show_endpoints/wsmisplanes').then(res => res.json());
                    const brand = await fetch('/api/get_brand').then(res => res.json());
                    const response = await fetch(`${endpoint.url_base}${endpoint.url_variable}?CUIT=${atob(documento)}&Brand=${brand.nombre}&TokenLogin=${tokenLogin}`, {
                        cache: "no-cache",
                    });
                    const json = await response.json();

                    if (!Object.entries(json).length) {
                        history.push('/login_mi_plan');
                        return;
                    }

                    const userPlans = json.Usuario.Datos.Planes.Plan;

                    setState(prevState => ({
                        ...prevState,
                        data: json,
                        planes: userPlans,
                        selectedPlan: null, // Ensure no plan is selected initially
                    }));
                } catch (error) {
                    console.error("Error fetching user data:", error);
                }
            };
            fetchUserData();
        }
    }, [documento, notificacionId, history, tokenLogin]);

    useEffect(() => {
        async function fetchReintegrosData() {
            // Only proceed if a plan is selected and necessary tokens are available
            if (!state.selectedPlan || !tokenLogin || !documento) {
                // If no plan is selected or tokens are missing, reset to default or do nothing
                setState(prevState => ({
                    ...prevState,
                    reintegros: { MuestraBoton: false, URL: "" },
                }));
                return;
            }

            try {
                const plan = state.selectedPlan;
                const reintegrosWS = await fetch('/api/show_endpoints/wsgetreintegros');
                const reintegrosEndpoint = await reintegrosWS.json();
                const timestamp = new Date().getTime();

                const reintegrosResponse = await fetch(`${reintegrosEndpoint.url_base}${reintegrosEndpoint.url_variable}?Token=${tokenLogin}&CUIT=${atob(documento)}&Grupo=${plan.Grupo}&Orden=${plan.Orden}&timestamp=${timestamp}`, {
                    cache: "no-cache",
                });

                if (!reintegrosResponse.ok) {
                    throw new Error(`HTTP error! status: ${reintegrosResponse.status}`);
                }

                const reintegrosData = await reintegrosResponse.json();

                setState(prevState => ({
                    ...prevState,
                    reintegros: reintegrosData,
                }));
            } catch (error) {
                console.error("Error fetching reintegros data:", error);
                // In case of an error during fetch, ensure reintegros state is still an object
                setState(prevState => ({
                    ...prevState,
                    reintegros: { MuestraBoton: false, URL: "" },
                }));
            }
        }

        fetchReintegrosData();
    }, [state.selectedPlan, tokenLogin, documento, setState]);


    useEffect(() => {
        fetchStatusFlags();
    }, []);

    useEffect(() => {
        if (state.selectedPlan) {
            fetchPlanDetails(state.selectedPlan);
        }
    }, [state.selectedPlan]);

    return (
        <AppContext.Provider value={[state, setState, setSelectedPlan, loadingPlanDetails]}>
            {children}
        </AppContext.Provider>
    );
};

export default AppProvider;
